import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Mail, ExternalLink } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { BRANCHES } from "@/data/salon";
import { useBooking } from "@/context/BookingContext";

export const Footer = () => {
  const { openBooking } = useBooking();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" data-testid="footer" className="bg-ink text-cream pt-24 pb-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-cream/10">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-5xl sm:text-6xl leading-[1] mb-6">Begin your <span className="italic text-pink">ritual.</span></h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => openBooking()}
                data-testid="footer-book-button"
                className="mt-2 rounded-full bg-pink px-8 py-4 text-xs uppercase tracking-[0.2em] text-ink hover:bg-pinklight transition-colors"
              >
                Book a treatment
              </button>
              <a
                href="https://www.treatwell.co.uk/place/fabrowz-motherwell/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-treatwell-link"
                className="mt-2 inline-flex items-center gap-2 rounded-full border border-cream/30 px-8 py-4 text-xs uppercase tracking-[0.2em] text-cream hover:border-pink hover:text-pink transition-colors"
              >
                Book on Treatwell <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
            <p className="mt-4 text-xs text-cream/50">View our full treatment menu & prices on Treatwell.</p>
          </div>

          <div className="lg:col-span-4 space-y-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-pink mb-3">Our Studios</p>
              {BRANCHES.map((b) => (
                <div key={b.id} className="flex items-start gap-3 mb-3">
                  <MapPin className="h-4 w-4 text-pink mt-1 shrink-0" />
                  <p className="text-sm text-cream/70"><span className="text-cream">{b.name}</span><br />{b.address}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-pink mt-1" />
              <p className="text-sm text-cream/70">Open daily · 09:30 – 18:00<br />Bookings & walk-ins welcome</p>
            </div>
            <a href="tel:+441412600122" className="flex items-start gap-3 hover:text-pink transition-colors">
              <Phone className="h-4 w-4 text-pink mt-1" />
              <span className="text-sm text-cream/70">0141 260 0122</span>
            </a>
            <a href="mailto:fabrowz0786@gmail.com" className="flex items-start gap-3 hover:text-pink transition-colors">
              <Mail className="h-4 w-4 text-pink mt-1" />
              <span className="text-sm text-cream/70">fabrowz0786@gmail.com</span>
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-pink mb-4">The Fabrowz Circle</p>
            <p className="text-sm text-cream/60 mb-4">Rituals, offers and quiet beauty notes.</p>
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="footer-newsletter-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full rounded-full bg-cream/10 border border-cream/20 px-5 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-pink outline-none"
              />
              <button
                data-testid="footer-newsletter-submit"
                disabled={loading}
                className="w-full rounded-full border border-pink px-5 py-3 text-xs uppercase tracking-[0.2em] text-pink hover:bg-pink hover:text-ink transition-colors disabled:opacity-50"
              >
                {loading ? "Joining…" : "Join the circle"}
              </button>
            </form>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-serif text-3xl tracking-[0.2em] text-pink">FABROWZ</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">© {new Date().getFullYear()} Fabrowz Beauty Atelier · Crafted with precision</p>
        </div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.08 }}
        viewport={{ once: true }}
        className="mt-10 text-center font-serif text-[18vw] leading-none tracking-tighter select-none pointer-events-none text-pink"
      >
        FABROWZ
      </motion.p>
    </footer>
  );
};
